import { Component, Inject, OnDestroy, Optional, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { BehaviorSubject, Subject } from 'rxjs';
import { Boat } from '@shared/models/boat';
import { U2bValidators } from '@shared/validators/validators';
import { U2bBoatValidators } from '@shared/validators/boat/boat-validators';
import { BoatApiService } from '@modules/bcm/@shared/services';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AppNotificationService } from '@core/services/app-notification.service';
import { isString } from '@shared/functions/is-string';
import { DEFAULT_DEBOUNCE_TIME } from '@modules/bcm/@shared/constants';
import { startOfToday } from '@core/date.facade';
import { isPlainObject } from '@shared/functions/is-plain-object';
import { TolgeeService } from '@core/translation/tolgee.service';
import { HttpParams } from '@angular/common/http';

@Component({
    selector: 'get-boat-dialog',
    templateUrl: './get-boat-dialog.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class GetBoatDialogComponent implements OnDestroy {
    private _unsubscribeAll = new Subject<void>();
    private searchTerm$ = new BehaviorSubject<string>('');

    boatForm: UntypedFormGroup;

    loadingBoats = true;
    isSearching = false;
    hasInitiallyLoaded = false;
    boats: Boat[] = [];
    filteredBoats$: BehaviorSubject<Boat[]> = new BehaviorSubject<Boat[]>([]);
    isSaving: boolean;

    showCheckbox = true;
    onlyBoatsWithoutOwner = true;
    showBoatOwnerInfo = false;
    currentOwnerName: string;

    constructor(
        public dialogRef: MatDialogRef<GetBoatDialogComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA) public data: {
            ownerName: string,
            boat?: Boat,
            showCheckbox?: boolean
            onlyBoatsWithoutOwner?: boolean
        },
        private _formBuilder: UntypedFormBuilder,
        private _boatApiService: BoatApiService,
        private _appNotificationService: AppNotificationService,
        private _tolgee: TolgeeService
    ) {
        this._createForm(this.data?.boat);

        if (this.data?.showCheckbox === false) { // must(!) check false, not falsy!
            this.onlyBoatsWithoutOwner = false;
            this.showCheckbox = false;
        } else {
            this.onlyBoatsWithoutOwner = this.data?.onlyBoatsWithoutOwner;
        }

        this._loadBoats();
    }

    save(): void {
        if (this.boatForm.invalid) {
            this.boatForm.markAllAsTouched();
            this._appNotificationService.showError(this._tolgee.translate('common.checkFieldsHighlight'));
            return;
        }

        this.isSaving = true;
        this.dialogRef.close(this.boatForm.getRawValue());
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    public displayBoatWith(boat: Boat): string {
        return boat ? [boat.licensePlate, boat.name, boat.manufacturer || boat.type].filter(_ => !!_).join(', ') : '';
    }

    private _createForm(boat?: Boat): void {
        this.boatForm = this._formBuilder.group({
            boat: [
                {value: boat, disabled: !!boat},
                [
                    U2bValidators.required(this._tolgee.translate('boats.selectRequired')),
                    U2bBoatValidators.boatExists(this._tolgee.translate('boats.listSelectInvalid'))
                ]
            ],
            isOwnerUntil: [
                startOfToday(),
                [
                    U2bValidators.required(this._tolgee.translate('common.validationMsg.dateRequired')),
                ]
            ]
        });

        this.boatForm.get('boat').valueChanges
            .pipe(
                takeUntil(this._unsubscribeAll),
                tap((currentBoat) => {
                    if (isPlainObject(currentBoat) && !!(currentBoat.owner || currentBoat.ownerCompany)) {
                        this.showBoatOwnerInfo = true;
                        this.currentOwnerName = (currentBoat.owner || currentBoat.ownerCompany).fullName;
                    } else {
                        this.showBoatOwnerInfo = false;
                        this.currentOwnerName = undefined;
                    }
                }),
                filter(value => isString(value)),
                debounceTime(DEFAULT_DEBOUNCE_TIME),
                distinctUntilChanged()
            )
            .subscribe(searchTerm => {
                this.searchTerm$.next(searchTerm);
            });
    }

    private _loadBoats(): void {
        this.boats = [];
        this.loadingBoats = true;

        this.searchTerm$
            .pipe(
                takeUntil(this._unsubscribeAll),
                tap(searchTerm => {
                    if (!this.hasInitiallyLoaded) {
                        this.loadingBoats = true;
                    } else {
                        this.isSearching = true;
                    }
                }),
                switchMap(searchTerm => {
                    const params = new HttpParams()
                        .set('page', '0')
                        .set('size', '100')
                        .set('filter', searchTerm || '');
                    return this._boatApiService.getAll(params).pipe(
                        map(page => page.results)
                    );
                }),
                tap(() => {
                    this.loadingBoats = false;
                    this.isSearching = false;
                    this.hasInitiallyLoaded = true;
                })
            )
            .subscribe(boats => {
                this.boats = boats;
                this.filteredBoats$.next(boats);
            });

        this.searchTerm$.next('');
    }
}
